/**
 * Easter kids' crafts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterKidsCraftsPack: ToilePack = {
    slug: 'easter-kids-crafts',
    category: 'season',
    name: "Easter kids' crafts",
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'bunny', label: 'Paper plate bunny', subject: 'A craft template sheet with bunny ears, eyes and whiskers to cut and glue on a plate, dashed cut lines, the steps shown only as pictures'},
        {key: 'chick', label: 'Handprint chick', subject: 'A craft sheet with a chick body and space for handprints as wings, a dotted guide, the steps shown only as pictures'},
        {key: 'egg', label: 'Cut and colour', subject: 'A craft sheet of three egg shapes with patterns to colour and cut, dashed cut lines, the steps shown only as pictures'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'pastel easter printable, egg and floral borders, soft rounded lettering, print-ready', palette: 'Soft pink, mint, lilac and butter yellow', framing: 'Laid out on a vertical page with an even white margin'},
        {key: 'vintage', label: 'Vintage', style: 'vintage easter printable, engraved floral borders, elegant serif lettering, print-ready', palette: 'Rose, sage, cream and a little gold', framing: 'Laid out on a cream page with an ornate border and a margin'},
        {key: 'kids', label: 'Kids', style: "kids' easter printable, bright cartoon bunnies and chicks, rounded lettering, print-ready", palette: 'Bright yellow, sky blue, grass green and pink', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['a duck instead of a chick', 'for toddlers, fewer pieces', 'as a paper-plate mask instead'],
    directionHint: 'try: a duck instead of a chick',
    seo: {
        title: "Easter Kids' Crafts: Bunny, Chick & Egg Templates",
        metaDescription: 'Nine Easter craft sheets for kids — a paper plate bunny, a handprint chick, and a cut-and-colour egg — with picture-only steps, print-ready.',
        h1: "Easter kids' crafts: bunny, chick, and egg templates",
        tagline: 'Nine sheets, three crafts, three styles each — steps shown as pictures, not words.',
        intro: "Three Easter craft sheets built for the classroom: a paper-plate bunny, a handprint chick, and a cut-and-colour egg, each with every step drawn as a picture instead of written instructions, in pastel, vintage, and kids' cartoon styles. Ask for a different animal, an older-kid version, or a group-project size, and the same craft grammar carries it.",
        useCases: [
            'a preschool or kindergarten craft session',
            'a Sunday school activity',
            'a rainy-day activity at home',
            'a classroom Easter party station',
        ],
        faq: [
            {q: 'Are the instructions written out, or shown as pictures?', a: 'Shown as pictures — the steps are drawn, not written, so pre-readers can follow along.'},
            {q: 'What size do these print at?', a: 'Each sheet is a single vertical page, close to US letter or A4 proportions.'},
            {q: 'Can I get a different animal or an older-kid version?', a: 'Yes — the direction field takes any variation, including a different animal or an older-kid layout.'},
            {q: 'What are the three styles?', a: "Pastel, vintage engraved-border, and bright kids' cartoon."},
        ],
    },
}
